// frontend/src/components/UniversalParameterSelector.js
// Universal parameter selection component with shopping cart interface

import React, { useState, useCallback, useMemo } from 'react';
import { 
  Input, 
  Card, 
  List, 
  Tag, 
  Button, 
  Space, 
  Typography, 
  Badge, 
  Tooltip, 
  Empty,
  Divider,
  Row,
  Col,
  Alert
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  DeleteOutlined,
  ShoppingCartOutlined,
  InfoCircleOutlined,
  ClearOutlined,
  CheckOutlined
} from '@ant-design/icons';
import { useParameterSearch } from '../hooks/useParameterSearch';
import { buildParameterRegistry } from '../utils/parameterSearchUtils';
import { message } from 'antd';

const { Text } = Typography;
const { Search } = Input;

/**
 * Universal Parameter Selector Component
 * Provides searchable interface for selecting parameters across all categories
 * with shopping cart functionality for multi-parameter selection
 */
const UniversalParameterSelector = ({
  onParameterSelectionChange,
  onProceedToDataEntry,
  selectedParameters = [],
  placeholder = "Search for any parameter (e.g., 'LDL cholesterol', 'blood pressure', 'heart rate')...",
  maxSelections = 100,
  style = {}
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [orderSetSearchQuery, setOrderSetSearchQuery] = useState('');

  // Use existing parameter search hook
  const {
    searchResults,
    isSearching,
    hasResults,
    handleSearchChange: hookHandleSearchChange,
  } = useParameterSearch(null, 25);

  // Handle search input changes
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    setShowSearchResults(value.trim().length >= 2);
    hookHandleSearchChange(value); // Call the hook's search handler
  }, [hookHandleSearchChange]);

  // Handle parameter addition to selection
  const handleAddParameter = useCallback((parameter) => {
    if (selectedParameters.length >= maxSelections) {
      return;
    }

    const isAlreadySelected = selectedParameters.some(p => p.key === parameter.key);
    if (isAlreadySelected) {
      return;
    }

    const newSelection = [...selectedParameters, parameter];
    onParameterSelectionChange(newSelection);
    
    // Clear search after selection
    setSearchQuery('');
    setShowSearchResults(false);
  }, [selectedParameters, maxSelections, onParameterSelectionChange]);

  // Handle parameter removal from selection
  const handleRemoveParameter = useCallback((parameterKey) => {
    const newSelection = selectedParameters.filter(p => p.key !== parameterKey);
    onParameterSelectionChange(newSelection);
  }, [selectedParameters, onParameterSelectionChange]);

  // Clear all selections
  const handleClearAll = useCallback(() => {
    onParameterSelectionChange([]);
  }, [onParameterSelectionChange]);

  // Build parameter registry for bulk selection
  const parameterRegistry = useMemo(() => {
    try {
      const registry = buildParameterRegistry();
      console.log('Parameter Registry Built:', {
        totalParameters: registry.length,
        categoryCounts: registry.reduce((acc, param) => {
          acc[param.category] = (acc[param.category] || 0) + 1;
          return acc;
        }, {}),
        sampleParameters: registry.slice(0, 5).map(p => ({ key: p.key, category: p.category, subcategory: p.subcategory }))
      });
      return registry;
    } catch (error) {
      console.error('Error building parameter registry for bulk selection:', error);
      return [];
    }
  }, []);

  // Handle bulk category selection
  const handleBulkCategorySelection = useCallback((categorySet) => {
    console.log('Bulk Category Selection Started:', {
      categorySet,
      registrySize: parameterRegistry.length,
      currentSelectionCount: selectedParameters.length
    });

    // Get all parameters for this category/subcategory from registry
    let categoryParameters = [];

    if (categorySet.subcategory) {
      // Filter by both category and subcategory
      categoryParameters = parameterRegistry.filter(param =>
        param.category === categorySet.category &&
        param.subcategory === categorySet.subcategory
      );
      console.log(`Filtering by category: ${categorySet.category} and subcategory: ${categorySet.subcategory}`, {
        foundParameters: categoryParameters.length,
        sampleKeys: categoryParameters.slice(0, 5).map(p => p.key)
      });
    } else {
      // Filter by category only
      categoryParameters = parameterRegistry.filter(param =>
        param.category === categorySet.category
      );
      console.log(`Filtering by category: ${categorySet.category}`, {
        foundParameters: categoryParameters.length,
        sampleKeys: categoryParameters.slice(0, 5).map(p => p.key)
      });
    }

    // Format parameters for display
    const formattedParameters = categoryParameters.map(param => ({
      key: param.key,
      displayName: param.displayName,
      label: param.displayName,
      category: param.category,
      subcategory: param.subcategory,
      units: param.units,
      type: param.type,
      options: param.options, // Include options for preset choices
      description: param.description
    }));

    // Filter out already selected parameters
    const newParameters = formattedParameters.filter(param =>
      !selectedParameters.some(selected => selected.key === param.key)
    );

    console.log('Bulk Selection Processing:', {
      totalFound: categoryParameters.length,
      afterFormatting: formattedParameters.length,
      newToAdd: newParameters.length,
      alreadySelected: formattedParameters.length - newParameters.length
    });

    // Check if we can add all parameters without exceeding limit
    const totalAfterAdd = selectedParameters.length + newParameters.length;
    if (totalAfterAdd > maxSelections) {
      message.warning(`Cannot add all ${newParameters.length} parameters. Would exceed limit of ${maxSelections}.`);
      return;
    }

    // Add all category parameters to selection
    const newSelection = [...selectedParameters, ...newParameters];
    onParameterSelectionChange(newSelection);

    message.success(`Added ${newParameters.length} ${categorySet.title} parameters to selection!`);
  }, [selectedParameters, maxSelections, onParameterSelectionChange, parameterRegistry]);

  // Get category color for visual organization
  const getCategoryColor = (category) => {
    const colorMap = {
      'Vitals': 'blue',
      'Labs': 'green',
      'Biometrics': 'orange',
      'Physical Exam': 'purple',
      'Imaging': 'cyan',
      'Lifestyle': 'magenta'
    };
    return colorMap[category] || 'default';
  };



  // Full category sets for bulk selection
  const fullCategorySets = useMemo(() => {
    if (!parameterRegistry || parameterRegistry.length === 0) {
      return [];
    }

    const categoryConfigs = {
      'Vitals': { icon: '🫀', subcategories: {} },
      'Labs': { icon: '🧪', subcategories: {} },
      'Biometrics': { icon: '💪', subcategories: {} },
      'Physical Exam': { icon: '🩺', subcategories: {} },
      'Imaging': { icon: '📸', subcategories: {} },
      'Lifestyle': { icon: '🌿', subcategories: {} }
    };

    // Group parameters by category and subcategory
    parameterRegistry.forEach(param => {
      if (categoryConfigs[param.category]) {
        const sub = param.subcategory || 'all';
        if (!categoryConfigs[param.category].subcategories[sub]) {
          categoryConfigs[param.category].subcategories[sub] = {
            count: 0,
            title: sub === 'all' ? `All ${param.category}` : param.subcategory
          };
        }
        categoryConfigs[param.category].subcategories[sub].count++;
      }
    });

    const sets = [];
    for (const category in categoryConfigs) {
      const config = categoryConfigs[category];
      // Add a card for the entire category
      sets.push({
        key: `${category.toLowerCase().replace(/\s+/g, '-')}-all`,
        title: `All ${category}`,
        description: `All ${category.toLowerCase()} parameters`,
        icon: config.icon,
        category: category,
        count: parameterRegistry.filter(p => p.category === category).length
      });

      // Add cards for subcategories
      for (const sub in config.subcategories) {
        if (sub !== 'all' && config.subcategories[sub].count > 1) {
          const subConfig = config.subcategories[sub];
          sets.push({
            key: `${category.toLowerCase()}-${sub.toLowerCase().replace(/\s+/g, '-')}-all`,
            title: subConfig.title,
            description: `All ${subConfig.title.toLowerCase()} parameters`,
            icon: config.icon,
            category: category,
            subcategory: sub,
            count: subConfig.count
          });
        }
      }
    }
    
    // Sort sets for consistent order: Vitals, Labs, Biometrics, etc.
    const categoryOrder = ['Vitals', 'Labs', 'Biometrics', 'Physical Exam', 'Imaging', 'Lifestyle'];
    sets.sort((a, b) => {
      const catA = categoryOrder.indexOf(a.category);
      const catB = categoryOrder.indexOf(b.category);
      if (catA !== catB) return catA - catB;
      if (a.title.startsWith('All ')) return -1; // 'All' comes first
      if (b.title.startsWith('All ')) return 1;
      return a.title.localeCompare(b.title); // Sort subcategories alphabetically
    });

    return sets;
  }, [parameterRegistry]);

  // Filter search results to exclude already selected parameters
  const filteredSearchResults = useMemo(() => {
    if (!searchResults) return [];
    return searchResults.filter(result =>
      !selectedParameters.some(selected => selected.key === result.key)
    );
  }, [searchResults, selectedParameters]);

  // Filter Order Sets based on search query
  const filteredOrderSets = useMemo(() => {
    if (!orderSetSearchQuery.trim()) {
      return []; // Show no Order Sets when no search query
    }

    const query = orderSetSearchQuery.toLowerCase().trim();
    return fullCategorySets.filter(categorySet => {
      return (
        categorySet.title.toLowerCase().includes(query) ||
        categorySet.description.toLowerCase().includes(query) ||
        categorySet.category.toLowerCase().includes(query) ||
        (categorySet.subcategory && categorySet.subcategory.toLowerCase().includes(query))
      );
    });
  }, [fullCategorySets, orderSetSearchQuery]);

  return (
    <div style={{ ...style }}>
      {/* Search Interface */}
      <Card 
        title={
          <Space>
            <SearchOutlined />
            <Text strong>Universal Parameter Search</Text>
            <Badge count={selectedParameters.length} showZero style={{ backgroundColor: '#52c41a' }} />
          </Space>
        }
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Search
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="large"
          loading={isSearching}
          allowClear
          style={{ marginBottom: 12 }}
        />

        {/* Search Results */}
        {showSearchResults && (
          <Card size="small" style={{ maxHeight: 300, overflowY: 'auto' }}>
            {filteredSearchResults.length > 0 ? (
              <List
                size="small"
                dataSource={filteredSearchResults}
                renderItem={(parameter) => (
                  <List.Item
                    actions={[
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        onClick={() => handleAddParameter(parameter)}
                        disabled={selectedParameters.length >= maxSelections}
                      >
                        Add
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <Text strong>{parameter.label}</Text>
                          <Tag color={getCategoryColor(parameter.category)} size="small">
                            {parameter.category}
                          </Tag>
                          {parameter.units && (
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              ({parameter.units})
                            </Text>
                          )}
                        </Space>
                      }
                      description={parameter.description}
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty 
                description="No parameters found" 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ margin: '20px 0' }}
              />
            )}
          </Card>
        )}
      </Card>



      {/* Enhanced Order Sets for Bulk Selection */}
      <Card
        title={
          <Space>
            <InfoCircleOutlined />
            <Text strong>📋 Order Sets - Bulk Parameter Selection</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              (Select entire categories for efficient data entry)
            </Text>
          </Space>
        }
        size="small"
        style={{ marginBottom: 16, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}
        extra={
          <Text type="secondary" style={{ fontSize: '11px' }}>
            💡 Tip: Use Order Sets to quickly select all parameters from a medical domain
          </Text>
        }
      >
        {/* Search and Filter for Order Sets */}
        <Row style={{ marginBottom: 16 }}>
          <Col span={24}>
            <Input.Search
              placeholder="Search Order Sets (e.g., 'vitals', 'labs', 'lipid')..."
              allowClear
              style={{ marginBottom: 8 }}
              value={orderSetSearchQuery}
              onChange={(e) => setOrderSetSearchQuery(e.target.value)}
              onSearch={(value) => setOrderSetSearchQuery(value)}
            />
            {orderSetSearchQuery.trim() && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {filteredOrderSets.length} Order Set(s) found for "{orderSetSearchQuery}"
              </Text>
            )}
          </Col>
        </Row>

        {/* Display Order Sets only when search query exists and has results */}
        {orderSetSearchQuery.trim() ? (
          filteredOrderSets.length > 0 ? (
            <Row gutter={[16, 16]}>
              {filteredOrderSets.map((categorySet) => {
            const alreadySelectedCount = selectedParameters.filter(p =>
              categorySet.subcategory ?
                (p.category === categorySet.category && p.subcategory === categorySet.subcategory) :
                (p.category === categorySet.category)
            ).length;

            const isDisabled = selectedParameters.length + categorySet.count > maxSelections;
            const isFullySelected = alreadySelectedCount === categorySet.count;

            return (
              <Col key={categorySet.key} xs={24} sm={12} md={8} lg={6}>
                <Card
                  size="small"
                  hoverable={!isDisabled}
                  style={{
                    opacity: isDisabled ? 0.6 : 1,
                    borderColor: isFullySelected ? '#52c41a' : (alreadySelectedCount > 0 ? getCategoryColor(categorySet.category) : '#d9d9d9'),
                    borderWidth: isFullySelected ? '2px' : '1px',
                    backgroundColor: isFullySelected ? '#f6ffed' : '#fff',
                    transition: 'all 0.3s ease'
                  }}
                  styles={{ body: { padding: '16px' } }}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space>
                        <Text style={{ fontSize: '20px' }}>{categorySet.icon}</Text>
                        <Text strong style={{ fontSize: '14px', color: isFullySelected ? '#52c41a' : '#000' }}>
                          {categorySet.title}
                        </Text>
                      </Space>
                      {isFullySelected && (
                        <Text style={{ fontSize: '16px', color: '#52c41a' }}>✅</Text>
                      )}
                    </Space>

                    <Text type="secondary" style={{ fontSize: '12px', lineHeight: '1.4' }}>
                      {categorySet.description}
                    </Text>

                    <Space justify="space-between" style={{ width: '100%', marginTop: '8px' }}>
                      <Tag color={getCategoryColor(categorySet.category)} size="small">
                        {categorySet.count} parameters
                      </Tag>
                      {alreadySelectedCount > 0 && (
                        <Tag color={isFullySelected ? 'success' : 'processing'} size="small">
                          {alreadySelectedCount}/{categorySet.count} selected
                        </Tag>
                      )}
                    </Space>

                    <Button
                      type={isFullySelected ? "default" : "primary"}
                      size="small"
                      block
                      disabled={isDisabled || isFullySelected}
                      onClick={() => handleBulkCategorySelection(categorySet)}
                      style={{
                        marginTop: '12px',
                        backgroundColor: isFullySelected ? '#f0f0f0' : undefined,
                        borderColor: isFullySelected ? '#d9d9d9' : undefined,
                        color: isFullySelected ? '#666' : undefined
                      }}
                      icon={isFullySelected ? <CheckOutlined /> : <PlusOutlined />}
                    >
                      {isFullySelected ? 'All Selected' : `Add All ${categorySet.count}`}
                    </Button>
                  </Space>
                </Card>
              </Col>
            );
          })}
            </Row>
          ) : (
            <Empty
              description="No Order Sets found"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ margin: '40px 0' }}
            >
              <Text type="secondary">
                Try searching for "vitals", "labs", "lipid", "biometrics", etc.
              </Text>
            </Empty>
          )
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              🔍 Search above to find and select Order Sets for bulk parameter selection
            </Text>
          </div>
        )}
      </Card>

      {/* Selected Parameters Shopping Cart */}
      <Card
        title={
          <Space>
            <ShoppingCartOutlined />
            <Text strong>Selected Parameters</Text>
            <Badge count={selectedParameters.length} showZero />
            {selectedParameters.length > 0 && (
              <Button 
                type="link" 
                size="small" 
                icon={<ClearOutlined />}
                onClick={handleClearAll}
                danger
              >
                Clear All
              </Button>
            )}
          </Space>
        }
        size="small"
        extra={
          selectedParameters.length > 0 && (
            <Button 
              type="primary" 
              icon={<ShoppingCartOutlined />}
              onClick={() => onProceedToDataEntry(selectedParameters)}
            >
              Enter Data ({selectedParameters.length})
            </Button>
          )
        }
      >
        {selectedParameters.length > 0 ? (
          <>
            {selectedParameters.length >= maxSelections && (
              <Alert
                message={`Maximum ${maxSelections} parameters selected`}
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
              />
            )}
            <List
              size="small"
              dataSource={selectedParameters}
              renderItem={(parameter) => (
                <List.Item
                  actions={[
                    <Tooltip title="Remove parameter">
                      <Button
                        type="link"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveParameter(parameter.key)}
                        danger
                        size="small"
                      />
                    </Tooltip>
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <Text strong>{parameter.label || parameter.displayName}</Text>
                        <Tag color={getCategoryColor(parameter.category)} size="small">
                          {parameter.category}
                        </Tag>
                        {parameter.units && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            ({parameter.units})
                          </Text>
                        )}
                      </Space>
                    }
                    description={parameter.description}
                  />
                </List.Item>
              )}
            />
          </>
        ) : (
          <Empty 
            description="No parameters selected" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ margin: '40px 0' }}
          >
            <Text type="secondary">
              Search for parameters above or use Order Sets to get started
            </Text>
          </Empty>
        )}
      </Card>
    </div>
  );
};

export default UniversalParameterSelector;
